import { Component } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EmailContents, Strings } from '@app/shared/constants';
import { ConfirmationDialogV2Component } from '@app/shared/dialog/confirmation-dialog-v2/confirmation-dialog-v2.component';
import { ActionType, ApplicationInsightEvent, GemisTenant } from '@app/shared/enums';
import { Email, EmailService } from '@app/shared/services/api/email.service';
import { UserService } from '@app/shared/services/api/user.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { UserStoreService } from '@app/shared/services/store/user-store.service';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { AuthService } from '@app/shared/services/auth.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { isNil } from 'lodash';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-delete-account-dialog',
  templateUrl: './delete-account-dialog.component.html',
  styleUrls: ['./delete-account-dialog.component.scss'],
})
export class DeleteAccountDialogComponent {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  formControlError: ValidationErrors = {};
  hasError = false;
  emailForm: FormGroup = this.formBuilder.group({
    emailControl: [
      '',
      {
        updateOn: 'blur',
        asyncValidators: [this.compareCurrentUserEmail()],
      },
    ],
  });

  constructor(
    private readonly dialogRef: MatDialogRef<DeleteAccountDialogComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly matDialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly appInsightsServiceDirective: AppInsightsServiceDirective,
    private readonly _emailService: EmailService,
    public authService: AuthService,
    public userService: UserService,
    public overlayLoader: OverlayLoaderService,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private userStoreService: UserStoreService
  ) {}

  get isGemisDSO(): boolean {
    return Object.values(GemisTenant).includes(environment.tenant as GemisTenant);
  }

  closeModal(): void {
    this.dialogRef.close();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  compareCurrentUserEmail(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      if (control.value.length <= 0) {
        this.formControlError = {
          required: true,
          message: this.translate.instant('module.userProfile.myPersonalData.deleteAccount.dialogRequiredErrorText'),
        };
      } else if (!Strings.emailRegex.test(control.value)) {
        this.formControlError = {
          pattern: true,
          message: this.translate.instant('module.userProfile.myPersonalData.deleteAccount.dialogPatternErrorText'),
        };
      } else if (control.value !== this.authService.currentUser.email) {
        this.formControlError = {
          incorrectEmail: true,
          message: this.translate.instant('module.userProfile.myPersonalData.deleteAccount.dialogEmailErrorText'),
        };
      } else {
        this.formControlError = {};
      }
      this.hasError = !isNil(this.formControlError);

      return of(this.formControlError);
    };
  }

  deleteAccount() {
    if (this.emailForm.invalid) {
      return;
    }
    this.overlayLoader.isLoading = true;
    this.userStoreService.user$
      .pipe(
        switchMap((user) =>
          this.userService.createServiceBusPayload({
            ...user,
            rolesCode: user.role?.code,
            municipalities: this.userMunicipalitiesService.municipalities.map((municipality) => ({
              name: municipality.name,
              municipalityKey: municipality.key,
            })),
            action: ActionType.delete,
          })
        ),
        switchMap((payload) => this.userService.postMessageToServiceBus(this.authService.currentUser.userId, payload)),
        switchMap(() => this.userService.deactivateUser(this.authService.currentUser.guid, this.authService.currentUser.email, this.authService.currentUser.roleName)),
        switchMap(() => this.userService.deleteCIAMUser(this.authService.currentUser.userId)),
        switchMap(() => this.sendUserEmailNotification()),
        catchError((err) => {
          this.showCiamDeleteErrorNotification();
          return throwError(() => err);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        this.overlayLoader.isLoading = false;
        this.dialogRef.close();

        this.matDialog
          .open(ConfirmationDialogV2Component, {
            data: {
              title: this.translate.instant('module.userProfile.myPersonalData.deleteAccount.dialogConfirmDeleteTitleText'),
              descriptionText: this.translate.instant('module.userProfile.myPersonalData.deleteAccount.dialogConfirmDeleteDescriptionText'),
              confirmText: this.translate.instant('module.userProfile.myPersonalData.deleteAccount.dialogConfirmDeleteButtonText'),
            },
            width: '434px',
            panelClass: ['-sm'],
          })
          .afterClosed()
          .subscribe(() => this.logout());
      });
  }

  sendUserEmailNotification() {
    const email: Email = {
      subject: this.translate.instant('messages.externalUserAccountDeletionEmailSubject'),
      templateId: EmailContents.ExternalUserAccountDeletion.templateId,
      recipient: this.authService.currentUser.email,
      data: JSON.stringify({
        Link: `${environment.energyPortalAppUrl}${this.isGemisDSO ? `public/user/registrieren` : `public/registrationrequest`}`,
      }),
    };

    return this._emailService.sendEmail(email).pipe(takeUntil(this.unsubscribe$));
  }

  showCiamDeleteErrorNotification() {
    this.overlayLoader.isLoading = false;
    this.matDialog.open(ConfirmationDialogV2Component, {
      data: {
        title: this.translate.instant('module.userProfile.myPersonalData.deleteAccount.dialogCIAMConfirmDeleteTitleText'),
        descriptionText: this.translate.instant('module.userProfile.myPersonalData.deleteAccount.dialogCIAMConfirmDeleteDescriptionText'),
        confirmText: this.translate.instant('module.userProfile.myPersonalData.deleteAccount.dialogCIAMConfirmDeleteButtonText'),
        hasError: true,
      },
      width: '430px',
      panelClass: ['-md'],
    });
  }

  logout() {
    this.overlayLoader.isLoading = true;
    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.logout);
    setTimeout(() => {
      this.authService.logout();
    }, 2000);
  }
}
